



























































































import { computed, defineComponent, ref, toRef, watch } from "@vue/composition-api";
import { FundraisingGoal } from "@scrile/api-provider/dist/api/FundraisingGoalsProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { Subscription } from "@scrile/api-provider/dist/api/SubscriptionProvider";
import { ContentFeedSearchResult } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useRole from "@/hooks/useRole";
import historyManager from "@/lib/historyManager";
import { $t } from "@/lib/i18nHelpers";
import { getAbsUrl, getProfileUrl, shareUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { BlockingStatus } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseIcon from "@/components/BaseIcon";
import BaseMenu from "@/components/BaseMenu";
import BasePage from "@/components/BasePage";
import BlockUserController from "@/components/BlockUserController";
import ConfirmSensitiveContent from "@/components/ConfirmSensitiveContent";
import ContentFeedView from "@/components/ContentFeedView";
import CreatePostButtonController from "@/components/CreatePostButtonController";
import EmptyContent from "@/components/EmptyContent";
import FeedNsfwNotice from "@/components/FeedNsfwNotice";
import PageTitle from "@/components/PageTitle";
import UserBookAppointmentsModal from "@/components/UserBookAppointmentModal";
import ProfileInfoView from "../ProfileInfoView";
import VerificationNotification from "../VerificationNotification";

export default defineComponent({
  name: "PageProfileView",
  components: {
    FeedNsfwNotice,
    UserBookAppointmentsModal,
    CreatePostButtonController,
    ConfirmSensitiveContent,
    ProfileInfoView,
    EmptyContent,
    BasePage,
    PageTitle,
    BaseIcon,
    BaseButton,
    BaseMenu,
    ContentFeedView,
    VerificationNotification,
    BlockUserController,
  },
  props: {
    canUpdateContentSet: { type: Boolean, required: true },
    feedId: { type: String },
    loading: { type: Boolean, required: true },
    scroll: { type: Boolean, required: true },
    isAuthor: { type: Boolean, required: true },
    isFollowing: { type: Boolean, required: true },
    isReceivingNotifications: { type: Boolean, default: false },
    user: { type: Object as () => UserPublicData },
    questionaryInfo: { type: Object as () => Record<string, any>, required: true },
    contentFeedSearchResult: { type: Object as () => ContentFeedSearchResult | null },
    subscriptionLevels: { type: Array as () => SubscriptionLevel[], required: true },
    paymentPackages: { type: Array as () => PaymentPackage[], required: true },
    activeSubscriptions: { type: Array as () => Subscription[], required: true },
    isBookingAvailable: { type: Boolean, required: true },
    activeGoal: { type: Object as () => FundraisingGoal | null },
  },
  setup(props, { root }) {
    const user = toRef(props, "user");

    const { isCreator } = useAuthUser();
    const { isAdmin: isUserAdmin } = useRole(user);

    const blockingStatus = ref<BlockingStatus>();
    const blockingConfirm = ref(false);

    const userId = computed(() => user.value?.id ?? "");
    const profileUrl = computed(() => (user.value && getProfileUrl(user.value)) || null);
    const isDisabled = computed(() => user.value?.disabled ?? true);
    const isBlocked = computed(() => blockingStatus.value?.authUser);
    const isSensitiveContent = computed(() => !user.value?.nsfwSettings?.safeForWork);
    const allowPinPosts = computed(() => config.featurePinPost);
    const showBookAppointmentsModal = ref(false);
    const allowCreatePost = computed(() => {
      if (!user.value) {
        return false;
      } else if (config.featureEveryoneCanPost) {
        return true;
      }
      return props.canUpdateContentSet;
    });
    const isEmptyProfile = computed(
      () => !props.loading && allowCreatePost.value && props.contentFeedSearchResult?.result.length === 0
    );
    const hasContentCount = computed(() =>
      props.subscriptionLevels.some(level => level.contentCount && level.contentCount > 0)
    );
    const menuItems = computed(() => {
      const menuItems = [];
      menuItems.push({
        name: "copy-link",
        text: $t("Copy link"),
      });
      if (!isUserAdmin.value && !props.isAuthor && isCreator.value) {
        menuItems.push({
          name: "block-user",
          text: $t(blockingStatus.value?.opponent ? $t("Unblock user") : $t("Block user")),
          prependIcon: "sci-blocked",
          prependIconColor: "var(--palette-negative)",
        });
      }
      return menuItems;
    });
    const hasHistory = computed(() => historyManager.hasHistory());
    const liveRoomId = computed(() => user.value?.olecamsUser?.externalId);

    const onFeedOnScreen = (feedId: string | null) => {
      if (feedId === props.feedId || !user.value) return;
      root.$router.replace(getProfileUrl(user.value, (feedId && { feedId }) || undefined));
    };

    const copyLink = () => shareUrl(getAbsUrl(`${root.$route.path}`), $t("Link copied"));

    watch(user, () => {
      showBookAppointmentsModal.value = false;
    });

    return {
      showBookAppointmentsModal,
      userId,
      profileUrl,
      isEmptyProfile,
      allowCreatePost,
      allowPinPosts,
      blockingStatus,
      blockingConfirm,
      isBlocked,
      isDisabled,
      isSensitiveContent,
      hasContentCount,
      menuItems,
      hasHistory,
      liveRoomId,
      onFeedOnScreen,
      copyLink,
      getProfileUrl,
    };
  },
});
